import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { pdf } from "@react-pdf/renderer";
import EstimateInvoicePDF from "./PDF/EstimateInvoicePDF";
import "./invoice.css";
import {
  PlusCircle,
  FileDown,
  RotateCcw,
  Save,
  ArrowLeft,
  Building2,
  User2,
  CalendarDays,
  Mail,
  Phone,
  MapPin,
  BadgeCheck,
} from "lucide-react";

const InvoiceTab = ({ estimateData, setEstimateData, setCurrentTab }) => {
  const { companyId, company } = useCompany();
  const [userId, setUserId] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({});
  const duration = 30;

  // resources passed from the previous tab
  const resources = useMemo(
    () => estimateData.resources || [],
    [estimateData.resources]
  );

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [totalOverheads, setTotalOverheads] = useState(0);
  const [others, setOthers] = useState({
    incidentals: 0,
    markup: 0,
    commission: 0,
    finders_fee: 0,
    tax: 0,
  });
  const [lines, setLines] = useState([]);

  const navigate = useNavigate();

  /**
   * If your DB stores rates like 7.5 (meaning 7.5%) or 20 (meaning 20%),
   * this converts to decimal form (0.075 or 0.2).
   * If the DB already stores decimals (0.2), it remains unchanged.
   */
  const normalizeRate = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n > 1 ? n / 100 : n;
  };

  // 0️⃣ Load user, invoice_items, overheads & others (UNCHANGED behavior, safer parsing)
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      // get user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.id) setUserId(user.id);

      // Fetch company_info
      const { data: ci, error: ciErr } = await supabase
        .from("company_info")
        .select(
          "company_name,email,address,phone_number,TIN,logo_url,bank_name,account_name,account_number,payment_instructions"
        )
        .eq("company_id", companyId)
        .maybeSingle();

      if (ciErr) console.error("company_info fetch error:", ciErr);
      setCompanyInfo(ci || {});

      // invoice_items for this company
      const { data: items = [] } = await supabase
        .from("invoice_items")
        .select("id, description, amount")
        .eq("company_id", companyId);

      setInvoiceItems(items);

      // overheads
      const { data: overheadRows = [] } = await supabase
        .from("overheads")
        .select("amount")
        .eq("company_id", companyId);

      const overheadSum = overheadRows.reduce(
        (sum, r) => sum + Number(r.amount || 0),
        0
      );
      setTotalOverheads(overheadSum);

      // others
      const { data: otherRow } = await supabase
        .from("others")
        .select("incidentals,markup,commission,finders_fee,tax,overhead_rate")
        .eq("company_id", companyId)
        .single();

      if (otherRow) setOthers(otherRow);
    })();
  }, [companyId, company?.owner_id]);

  // 1️⃣ Calculate costs (UPDATED: normalize rates)
  const totalResources = useMemo(
    () => resources.reduce((sum, r) => sum + Number(r.total || 0), 0),
    [resources]
  );

  const overheadRate = useMemo(
    () => normalizeRate(others.overhead_rate),
    [others.overhead_rate]
  );

  const totalOverheadsAllocated = useMemo(() => {
    const pd = Number(estimateData.projectDuration) || 0;
    return ((overheadRate * totalOverheads) / duration) * pd;
  }, [overheadRate, totalOverheads, estimateData.projectDuration]);

  const incidentalsRate = useMemo(
    () => normalizeRate(others.incidentals),
    [others.incidentals]
  );
  const markupRate = useMemo(
    () => normalizeRate(others.markup),
    [others.markup]
  );
  const commissionRate = useMemo(
    () => normalizeRate(others.commission),
    [others.commission]
  );
  const findersFeeRate = useMemo(
    () => normalizeRate(others.finders_fee),
    [others.finders_fee]
  );
  const taxRate = useMemo(() => normalizeRate(others.tax), [others.tax]);

  const totalIncidentals = useMemo(
    () => incidentalsRate * totalResources,
    [incidentalsRate, totalResources]
  );

  const totalCost = useMemo(
    () => totalResources + totalOverheadsAllocated + totalIncidentals,
    [totalResources, totalOverheadsAllocated, totalIncidentals]
  );

  const totalMarkup = useMemo(
    () => markupRate * totalCost,
    [markupRate, totalCost]
  );

  const finalCost = useMemo(
    () => totalCost + totalMarkup,
    [totalCost, totalMarkup]
  );

  const totalCommission = useMemo(
    () => commissionRate * finalCost,
    [commissionRate, finalCost]
  );

  const findersFee = useMemo(
    () => findersFeeRate * finalCost,
    [findersFeeRate, finalCost]
  );

  /**
   * "price" here is your pre-tax selling price (still includes commission + finders fee),
   * and is what we distribute across invoice lines.
   */
  const price = useMemo(
    () => finalCost + totalCommission + findersFee,
    [finalCost, totalCommission, findersFee]
  );

  // 2️⃣ Build invoice lines (UPDATED: distribute price using weights; avoid multiplying by raw amounts)
  useEffect(() => {
    const rawWeights = invoiceItems.map((item) => {
      const n = Number(item.amount || 0);
      if (!Number.isFinite(n) || n <= 0) return 0;

      // if stored as 30 (meaning 30%), normalize to 0.3
      return n > 1 ? n / 100 : n;
    });

    const weightSum = rawWeights.reduce((s, w) => s + w, 0);

    const built = invoiceItems.map((item, idx) => {
      const baseWeight = rawWeights[idx] || 0;

      // If weights are missing/zero, distribute equally.
      const share =
        weightSum > 0
          ? baseWeight / weightSum
          : invoiceItems.length > 0
          ? 1 / invoiceItems.length
          : 0;

      const unitAmount = share * price;

      return {
        id: item.id,
        description: item.description,
        quantity: 1,
        // keep baseAmount field for your existing quantity logic
        baseAmount: unitAmount,
        amount: unitAmount * 1,
      };
    });

    setLines(built);
  }, [invoiceItems, price]);

  const handleLineChange = (idx, field, val) => {
    const updated = [...lines];

    if (!updated[idx]) return;

    if (field === "description") {
      updated[idx].description = val;
    } else if (field === "quantity") {
      const q = Math.max(1, Number(val) || 1);
      updated[idx].quantity = q;
      updated[idx].amount = (Number(updated[idx].baseAmount) || 0) * q;
    }

    setLines(updated);
  };

  const subTotal = useMemo(
    () => lines.reduce((sum, l) => sum + Number(l.amount || 0), 0),
    [lines]
  );

  const tax = useMemo(() => taxRate * subTotal, [taxRate, subTotal]);
  const taxPrice = useMemo(() => subTotal + tax, [subTotal, tax]);

  // 3️⃣ Save invoice & estimate (UNCHANGED structure, but totals now match the lines)
  const saveInvoice = async () => {
    if (!userId || !companyId) return;

    const items = lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPrice: Number(l.amount || 0) / Math.max(1, Number(l.quantity || 1)),
      lineTotal: l.amount,
    }));

    const invoicePayload = {
      user_id: userId,
      company_id: companyId,
      customer: estimateData.customerName,
      address: estimateData.customerAddress,
      date: estimateData.date,
      expiryDate: estimateData.expiryDate,
      invoiceNumber: estimateData.estimateNumber,
      items,
      overhead: totalOverheadsAllocated,
      incidentals: totalIncidentals,
      markup: totalMarkup,
      commission: totalCommission,
      finders_fee: findersFee,
      vat_amount: tax,
      subtotal: subTotal,
      total: taxPrice,
      notes: estimateData.notes,
      status: "Pending",
      currency: "NGN",
    };

    const estimatePayload = {
      customer_id: userId, // (kept as-is per your code comment)
      company_id: companyId,
      customer_name: estimateData.customerName,
      address: estimateData.customerAddress,
      date: estimateData.date,
      expiryDate: estimateData.expiryDate,
      Estimates_ID: estimateData.estimateNumber,
      items,
      overhead: totalOverheadsAllocated,
      incidentals: totalIncidentals,
      markup: totalMarkup,
      commission: totalCommission,
      finders_fee: findersFee,
      vat_amount: tax,
      subtotal: subTotal,
      total: taxPrice,
      status: "Pending",
    };

    const { error: invError } = await supabase
      .from("invoices")
      .insert([invoicePayload])
      .select("id")
      .single();

    if (invError) {
      console.error("Error saving invoice:", invError);
      return alert("Failed to save invoice.");
    }

    const { error: estError } = await supabase
      .from("estimates")
      .insert([estimatePayload])
      .select("id")
      .single();

    if (estError) {
      console.error("Error saving estimate:", estError);
      return alert("Invoice saved but failed to save estimate.");
    }

    alert("Invoice and Estimate saved successfully!");
    navigate("/totalReceipts");
  };

  const printInvoice = async () => {
    try {
      const accent =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--primary-color")
          .trim() || "#008080";

      const invoiceDataForPDF = {
        doc_title: "INVOICE",
        customer: estimateData.customerName || "",
        address: estimateData.customerAddress || "",
        date: estimateData.date || "",
        expiryDate: estimateData.expiryDate || "",
        invoiceNumber: estimateData.estimateNumber || "INV",
        notes: estimateData.notes || "",

        // IMPORTANT: stamp logic checks "paid"
        status:
          (estimateData.status || "").toLowerCase() === "paid"
            ? "paid"
            : "pending",

        vat_rate: taxRate || 0,
        vat_amount: tax || 0,
        subtotal: subTotal || 0,
        grand_total: taxPrice || 0,
      };

      const itemsForPDF = (lines || []).map((l) => {
        const qty = Math.max(1, Number(l.quantity || 1));
        const lineTotal = Number(l.amount || 0);
        const unitPrice = qty > 0 ? lineTotal / qty : lineTotal;

        return {
          description: l.description,
          quantity: qty,
          unitPrice,
          lineTotal,
        };
      });

      const doc = (
        <EstimateInvoicePDF
          companyInfo={companyInfo}
          invoiceData={invoiceDataForPDF}
          items={itemsForPDF}
          accent={accent}
          currency="NGN"
        />
      );

      const blob = await pdf(doc).toBlob();
      const fileName = `${estimateData.estimateNumber || "invoice"}.pdf`;
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Could not generate PDF. Please try again.");
    }
  };

  const resetForm = () => setLines([]);
  const handlePrev = () => setCurrentTab(2);

  return (
    <div className="invoice-shell">
      {/* LEFT: Editor */}
      <section className="invoice-input panel">
        <header className="inv-head">
          <div className="inv-title">
            <h2>Create/Edit Invoice</h2>
            <p className="muted">
              <Building2 size={14} /> {companyInfo.company_name || "—"}{" "}
              &nbsp;•&nbsp;
              <User2 size={14} /> {estimateData.customerName || "—"}{" "}
              &nbsp;•&nbsp;
              <CalendarDays size={14} /> {estimateData.date || "—"}
            </p>
          </div>
          <div className="inv-actions">
            <button className="btn-ghost" onClick={handlePrev}>
              <ArrowLeft size={16} /> Previous
            </button>
            <button className="btn-primary" onClick={saveInvoice}>
              <Save size={16} /> Save
            </button>
            <button className="icon-btn" title="Reset" onClick={resetForm}>
              <RotateCcw size={16} />
            </button>
            <button
              className="icon-btn"
              title="Download PDF"
              onClick={printInvoice}
            >
              <FileDown size={16} />
            </button>
          </div>
        </header>

        {/* Items */}
        <div className="inv-card">
          <div className="card-head">
            <h3>Items</h3>
            {lines.length === 0 && (
              <button
                className="btn-ghost"
                onClick={() => navigate("/settings/invoice-items")}
              >
                <PlusCircle size={16} /> Add Invoice Items
              </button>
            )}
          </div>

          {lines.length > 0 && (
            <div className="lines">
              {lines.map((l, i) => (
                <div key={i} className="line-row">
                  <input
                    type="text"
                    value={l.description}
                    id="edit-input"
                    placeholder="Item description"
                    onChange={(e) =>
                      handleLineChange(i, "description", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    min="1"
                    value={l.quantity}
                    id="edit-input"
                    onChange={(e) =>
                      handleLineChange(i, "quantity", e.target.value)
                    }
                  />
                  <span className="line-amount">
                    ₦{Number(l.amount || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="inv-grid">
          <div className="inv-card">
            <h3>Totals</h3>
            <ul className="kv">
              <li>
                <span>Sub-Total</span>
                <strong>₦{subTotal.toFixed(2)}</strong>
              </li>
              <li>
                <span>Overheads</span>
                <strong>₦{totalOverheadsAllocated.toFixed(2)}</strong>
              </li>
              <li>
                <span>Incidentals</span>
                <strong>₦{totalIncidentals.toFixed(2)}</strong>
              </li>
              <li>
                <span>Markup</span>
                <strong>₦{totalMarkup.toFixed(2)}</strong>
              </li>
              <li>
                <span>Commission</span>
                <strong>₦{totalCommission.toFixed(2)}</strong>
              </li>
              <li>
                <span>Finder’s Fee</span>
                <strong>₦{findersFee.toFixed(2)}</strong>
              </li>
              <li>
                <span>VAT</span>
                <strong>₦{tax.toFixed(2)}</strong>
              </li>
              <li className="grand">
                <span>Total</span>
                <strong>₦{taxPrice.toFixed(2)}</strong>
              </li>
            </ul>
          </div>

          <div className="inv-card">
            <h3>Notes</h3>
            <textarea
              placeholder="Payment terms, delivery, or any details for the client…"
              value={estimateData.notes || ""}
              onChange={(e) =>
                setEstimateData({ ...estimateData, notes: e.target.value })
              }
            />
          </div>
        </div>
      </section>

      {/* RIGHT: Live Preview (legacy IDs preserved) */}
      <aside id="invoicePreview" className="invoice-preview panel">
        {companyInfo.logo_url && (
          <img src={companyInfo.logo_url} alt="Logo" className="logo" />
        )}
        <div id="comp-div">
          <p id="comp-phone">
            {" "}
            <Phone size={8} /> <span>{companyInfo.phone_number}</span>
          </p>
          <p id="comp-email">
            {" "}
            <Mail size={8} /> <span>{companyInfo.email}</span>
          </p>
          <p id="comp-addr">
            <MapPin size={8} /> <span>{companyInfo.address}</span>
          </p>
          <p id="comp-tin">
            {" "}
            <BadgeCheck size={8} /> <span> {companyInfo.TIN} </span>
          </p>
        </div>

        <div className="cust-div">
          <h3 id="cust-title">Recipient Information</h3>
          <p id="cust-name">
            <strong>Bill To:</strong> {estimateData.customerName || "—"}
          </p>
          <p id="cust-addr">
            <strong>Address:</strong> {estimateData.customerAddress || "—"}
          </p>
        </div>

        <div className="invoice-details" data-status={estimateData.status}>
          <p>
            <strong>Estimate #:</strong> {estimateData.estimateNumber}
          </p>
          <p>
            <strong>Date:</strong> {estimateData.date}
          </p>
          <p>
            <strong>Due:</strong> {estimateData.expiryDate}
          </p>
        </div>

        <h3 id="cust-title">Description of Services Provided</h3>

        <div className="table-wraps">
          <table className="invoice-items">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Quantity</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.description}</td>
                  <td className="num">{l.quantity}</td>
                  <td className="num">
                    ₦{Number(l.amount || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bottom">
          {estimateData.notes && (
            <p id="inv-notes" className="notes">
              <strong>Notes:</strong>
              <br />
              <div className="notes-body preserve">{estimateData.notes}</div>
            </p>
          )}

          <div className="invoice-totals">
            <p>Sub-Total: ₦{Number(subTotal || 0).toLocaleString()}</p>
            <p>VAT: ₦{Number(tax || 0).toLocaleString()}</p>
            <p className="grand">
              <strong>Total: ₦{Number(taxPrice || 0).toLocaleString()}</strong>
            </p>
          </div>
        </div>

        <small id="footer" className="muted-center">
          Thank you for Your Business ...
        </small>
      </aside>
    </div>
  );
};

export default InvoiceTab;
